import { useMemo, useState } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Manage from "@/v2-backoffice-client/components/manage"
import { useLicenseQuery } from "@/v2-backoffice-client/license/license.query"
import { useLicenseStatusesQuery } from "@/v2-backoffice-client/license/license.statuses.query"
import { useLicenseAddOnsQuery } from "@/v2-backoffice-client/license/addOns/license.addOns.query"
import {
  carryingLicensesOf,
  isLicenseLive,
} from "@/v2-backoffice-client/license/addOns/license.addOns.utils"
import LicenseAddOnManageAvailableItem from "@/v2-backoffice-client/license/addOns/manage/LicenseAddOnManageAvailableItem"
import LicenseAddOnManageHeldItem from "@/v2-backoffice-client/license/addOns/manage/LicenseAddOnManageHeldItem"

export type LicenseAddOnManageDialogProps = {
  customerId: number
  customerName?: string | null
  open: boolean
  onOpenChange(open: boolean): void
}

function matches(name: string, search: string) {
  return !search || name.toLowerCase().includes(search)
}

// The customer's add-ons, managed the way teams are managed for a user: a
// search, the add-ons they have (with Remove), the add-ons they could have
// (with Add), every click a write. Nothing else is asked, because an add-on
// has no settings of its own: it goes with a product, and whichever license
// the customer holds for that product carries it. Each row says whether the
// add-on works for this customer, and the hover says why or why not.
function LicenseAddOnManageDialog(props: LicenseAddOnManageDialogProps) {
  const { customerId, customerName, open, onOpenChange } = props
  const who = customerName ?? "the customer"
  const [search, setSearch] = useState("")

  // One read for both sections (the Add-ons table under the licenses runs
  // the same one, so the dialog opens from the cache and a write refreshes
  // both); the customer's licenses decide which add-ons work at once.
  const { data, loading, error } = useLicenseAddOnsQuery({
    variables: { customerId },
    skip: !open,
  })
  const { data: licenseData, loading: licensesLoading } = useLicenseQuery({
    variables: { customerId },
    skip: !open,
  })
  const { data: statusesData } = useLicenseStatusesQuery()
  const statusNamesByCode = useMemo(
    () =>
      new Map(
        (statusesData?.getLicenseStatuses ?? []).map((status) => [
          status.Code,
          status.Name,
        ]),
      ),
    [statusesData?.getLicenseStatuses],
  )
  function statusNameOf(code: number) {
    return statusNamesByCode.get(code)
  }

  const licenses = licenseData?.getLicenses ?? []
  const catalog = data?.getAddOns ?? []
  const held = data?.getCustomerAddOns ?? []

  // The add-ons the customer could add, the ones that work at once first.
  const available = useMemo(() => {
    const ranked = catalog
      .filter((addOn) => !addOn.IsHeld)
      .map((addOn) => {
        const carriers = carryingLicensesOf(addOn, licenses)
        return { addOn, carriers, isReady: carriers.some(isLicenseLive) }
      })
    return ranked.sort(
      (a, b) =>
        Number(b.isReady) - Number(a.isReady) ||
        a.addOn.Name.localeCompare(b.addOn.Name),
    )
  }, [catalog, licenses])

  const searchLower = search.trim().toLowerCase()
  const visibleHeld = held.filter((addOn) =>
    matches(addOn.AddOnName, searchLower),
  )
  const visibleAvailable = available.filter((choice) =>
    matches(choice.addOn.Name, searchLower),
  )

  function handleOpenChange(nextOpen: boolean) {
    onOpenChange(nextOpen)
    if (!nextOpen) setSearch("")
  }

  return (
    <Manage.Dialog
      open={open}
      onOpenChange={handleOpenChange}
      title={`Add-ons for ${who}`}
    >
      <Manage.Search
        placeholder="Search add-ons by name..."
        onSearch={setSearch}
      />

      <ErrorMessage className="my-2 text-center" error={error} />

      {loading || licensesLoading ? (
        <Manage.Skeleton count={4} aria-busy="true" />
      ) : (
        <>
          {visibleHeld.length > 0 && (
            <Manage.Section
              label={`Add-ons ${who} has (${visibleHeld.length})`}
            >
              <Manage.List>
                {visibleHeld.map((addOn) => (
                  <LicenseAddOnManageHeldItem
                    key={addOn.AddOnId}
                    addOn={addOn}
                    customerId={customerId}
                    who={who}
                    statusNameOf={statusNameOf}
                  />
                ))}
              </Manage.List>
            </Manage.Section>
          )}

          <Manage.Section
            label={`Add-ons not yet added (${visibleAvailable.length})`}
            className={visibleHeld.length > 0 ? "border-t border-border" : ""}
          >
            {catalog.length === 0 ? (
              <Manage.Empty>
                No add-ons in the catalog yet. They are made under Licensing →
                Add-ons.
              </Manage.Empty>
            ) : visibleAvailable.length === 0 ? (
              <Manage.Empty>
                {searchLower
                  ? "No add-on by that name"
                  : `${who} has every add-on in the catalog`}
              </Manage.Empty>
            ) : (
              <Manage.List variant="results">
                {visibleAvailable.map((choice) => (
                  <LicenseAddOnManageAvailableItem
                    key={choice.addOn.Id}
                    addOn={choice.addOn}
                    carriers={choice.carriers}
                    customerId={customerId}
                    who={who}
                    statusNameOf={statusNameOf}
                  />
                ))}
              </Manage.List>
            )}
          </Manage.Section>
        </>
      )}
    </Manage.Dialog>
  )
}

export default LicenseAddOnManageDialog
